import { produce } from 'immer';
import mitt from 'mitt';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';

// Data needed to render UX and to validate
export type LogDeploymentInfo = {
  accountIdentifier: string;
  projectID: number;
  projectName: string;
  deploymentID: number;
  deploymentName: string;
  deploymentStage: string;
  components: { componentName: string; componentUrl: string; namespace: string }[];
};

type ContainerLogSettings = {
  type: 'container';
  url: string;
  title: string;
  deployment: LogDeploymentInfo;
  componentName: string;
  componentUrl: string;
  namespace: string;
  pod: string | null;
  container: string | null;
};

type CommandLogSettings = {
  type: 'command';
  url: string;
  title: string;
  deployment: LogDeploymentInfo;
  componentName: string;
  componentUrl: string;
  namespace: string;
  commandIdentifier: string;
};

type AdminLogSettings = {
  type: 'admin';
  url: string;
  title: string;
  deployment: LogDeploymentInfo | null; // We could have special logs that are not tied to deployments, eg. SaaS log or other debugging stuff
  adminLogIdentifier: string; //'helm' | 'core' | 'system' | 'core-config'
};

export type LogSettings = ContainerLogSettings | CommandLogSettings | AdminLogSettings;

export const isContainerLog = (settings: LogSettings): settings is ContainerLogSettings => settings.type === 'container';
export const isCommandLog = (settings: LogSettings): settings is CommandLogSettings => settings.type === 'command';
export const isAdminLog = (settings: LogSettings): settings is AdminLogSettings => settings.type === 'admin';

export type ActiveLog = {
  settings: LogSettings;
  jobUUID: string | null;
  state: 'loading' | 'running' | 'error' | null;
  errorMessage: string | null;
  sidebarLogs: any[]; // ???, might need to be very dynamic}
};

export interface LogState {
  // Current active log, this should contain all data needed to render the log
  activeLog: ActiveLog | null;
  dialog: {
    open: boolean;
    fullscreen: boolean;
  };
  logs: Map<string, string[]>; // jobUUID => string[]
}

export type OnLog = (params: { type: 'container'; level1: string; level2?: string; level3?: string }) => void;

const initialState: LogState = {
  activeLog: null,
  dialog: {
    open: false,
    fullscreen: false,
  },
  logs: new Map(),
};

const logEmitter = mitt<{ [jobUUID: string]: string }>();

/** Hold state for active log and log dialog
 *  NOTE! In general don't use this directly, use useLog instead
 */
export const useLogState = create(
  combine(initialState, (set, get) => ({
    /** Save a new log entry */
    appendLogData: ({ jobUUID, data }: { jobUUID: string; data: string }) =>
      set(
        produce((state: LogState) => {
          if (!state.logs.has(jobUUID)) state.logs.set(jobUUID, []);
          //console.log('ADDING', jobUUID, data);
          state.logs.get(jobUUID)?.push(data);

          //console.log('NOW WE HAVE', state.logs.get(jobUUID));
          logEmitter.emit(jobUUID, data);
        })
      ),

    /** Load all previously logged entries */
    load: (jobUUID: string) => get().logs.get(jobUUID) || [],

    /** Listen to incoming logs on a certain ID */
    subscribe: (jobUUID: string, callback: (entry: string) => void) => {
      //console.log('SUBSCRIBE', jobUUID);
      logEmitter.on(jobUUID, callback);
    },

    /** Stop listening to incoming logs on a certain ID */
    unsubscribe: (jobUUID: string, callback: (entry: string) => void) => logEmitter.off(jobUUID, callback),

    /** Apply partial update to the default values */
    initActiveLog: (settings: LogSettings) =>
      set(
        produce((state: LogState) => {
          state.activeLog = {
            settings,
            jobUUID: null,
            state: 'loading',
            errorMessage: null,
            sidebarLogs: [],
          };
        })
      ),

    /** Update part of the active log */
    updateActiveLog: (partial: Partial<ActiveLog>) =>
      set(
        produce((state: LogState) => {
          // TODO: Should we do something if activeLog is empty?
          if (state.activeLog)
            state.activeLog = {
              ...state.activeLog,
              ...partial,
            };
        })
      ),

    showLogDialog: () =>
      set(
        produce((state: LogState) => {
          state.dialog.open = true;
          state.dialog.fullscreen = false;
        })
      ),

    hideLogDialog: () =>
      set(
        produce((state: LogState) => {
          state.dialog.open = false;
        })
      ),

    toggleFullscreen: () =>
      set(
        produce((state: LogState) => {
          state.dialog.fullscreen = !state.dialog.fullscreen;
        })
      ),
  }))
);
